import { TrendingUpDown } from '@tamagui/lucide-icons';
import { ScrollView } from '@unistocks/components/basic/scrollview';
import { ErrorMessage } from '@unistocks/components/error-message/error-message';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { getAssetDetailsHref } from '@unistocks/features/assets/asset-routes';
import { getCalculatedValueAssetId } from '@unistocks/features/dashboard/components/calculated-value/asset-id';
import {
  DashboardItemFrame,
  DashboardItemProps
} from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { DashboardItemHeader } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item-header';
import { ValueListRow } from '@unistocks/features/dashboard/components/value-list-item/value-list-row';
import { ValueListTable } from '@unistocks/features/dashboard/components/value-list-table/value-list-table';
import { t } from '@unistocks/localization';
import { AssetListDataDto, AssetListType } from '@unistocks/proxy/axios-client';
import {
  dashboardGetAssetListDataQueryKey,
  useDashboardGetAssetListDataQuery
} from '@unistocks/proxy/axios-client/DashboardQuery';
import { mapEnumToOptions } from '@unistocks/shared/utils/enum-utils';
import { dateConstantOptions } from '@unistocks/shared/utils/enums/date-constant';
import { useRouter } from 'expo-router';
import React, { useCallback } from 'react';

const assetListTypeOptions = mapEnumToOptions(AssetListType, 'AssetListType');

export const AssetList: React.FC<DashboardItemProps> = ({ item, openSettings, ...frameProps }) => {
  const router = useRouter();
  const { id, settings } = item;
  const { data, isSuccess } = useDashboardGetAssetListDataQuery(
    {
      id
    },
    {
      select: useCallback(
        (data: AssetListDataDto) => {
          const firstList =
            settings.listType === AssetListType.Both
              ? data.items.filter(item => item.value >= 0)
              : data.items;
          const secondList =
            settings.listType === AssetListType.Both
              ? data.items.filter(item => item.value < 0)
              : undefined;

          return [firstList, secondList] as const;
        },
        [settings.listType]
      )
    }
  );

  const range = dateConstantOptions.find(option => option.value === settings.period)!;
  const type = assetListTypeOptions.find(option => option.value === settings.listType)!;
  const title = settings.title || `${t(type.key)} - ${t(range.key)}`;

  return (
    <DashboardItemFrame {...frameProps}>
      <DashboardItemHeader item={item} title={title} openSettings={openSettings} />
      <QueryState
        queryKeys={[dashboardGetAssetListDataQueryKey(id)]}
        size={'$5'}
        flex={1}
        py={0}
        renderLoading={
          <Loader size={'$5'} textProps={{ size: '$6' }}>
            {t('AbpUi::LoadingWithThreeDot')}
          </Loader>
        }
        renderError={({ error, onRetry }) => <ErrorMessage error={error} onRetry={onRetry} />}
        isEmpty={isSuccess && !data[0].length && !data[1]?.length}
        noDataIcon={TrendingUpDown}
        noDataText={t('::Dashboard:AssetList:NoData')}
      >
        {isSuccess ? (
          <ScrollView showsVerticalScrollIndicator={false} $platform-web={{ h: '80%' }}>
            <ValueListTable
              firstList={data[0]}
              secondList={data[1]}
              visibleItemsLimit={5}
              getRowOnPress={value => {
                const assetId = getCalculatedValueAssetId(value);

                return assetId ? () => router.push(getAssetDetailsHref(assetId)) : undefined;
              }}
              renderRow={value => <ValueListRow calculatedValue={value} valueCellWidth={'$6'} />}
            />
          </ScrollView>
        ) : null}
      </QueryState>
    </DashboardItemFrame>
  );
};

AssetList.displayName = 'AssetList';
